import { addressFieldNames, addressFieldNamesSet } from '@/helpers/AddressHelpers'
import { appendUniqueItems, prependUniqueItems } from '@/helpers/ArrayHelpers'
import environment from '@/helpers/Environment'
import { builtInFields, customFields, IdentityFormData } from '@/helpers/IdentityHelpers'
import { csvToArray, toKebabCase } from '@/helpers/StringHelpers'

/**
 * Ordered fields to render.
 */
export function getIdentityFormFields(
  answers: IdentityFormData,
  configs: string[],
  omit: string[],
  requireAllAddressFields: boolean = false,
): FormInputField[] {
  const result: FormInputField[] = []

  const fields = getAvailableFields(answers)
  const names = getFieldNames(configs, omit)
  const fieldNames = requireAllAddressFields ? addAllAddressFields(names) : names

  for (const name of fieldNames) {
    const field = fields[name]
    if (field) {
      const className = toKebabCase(field.key)
      result.push({
        ...field,
        required: requireAllAddressFields && addressFieldNamesSet.has(name) ? true : field.required,
        // TODO Remove the dynamic class name that is not namespaced.
        className: `${className} field-${className}`,
      })
    } else {
      /* eslint-disable no-console */
      console.error(`Custom identity field '${name}' is not defined.`)
    }
  }

  return result
}
/**
 * If all of the address fields are present then do nothing.
 * If none of the address fields are present then add them all to the end of the form.
 * If at least 1 of the address fields are present then remove all address fields and insert them at the index of the first address field.
 */
export function addAllAddressFields(fields: string[]): string[] {
  // Need to clone set so that the original is not modified.
  const remainder = new Set(addressFieldNamesSet)
  const result: string[] = []
  for (let i = 0; i < fields.length; i++) {
    const name = fields[i]
    if (remainder.has(name)) {
      if (remainder.size === 5) {
        result.push(...addressFieldNames)
      }
      remainder.delete(name)
    } else {
      result.push(name)
    }
  }

  if (remainder.size === 0) {
    return fields
  } else if (remainder.size === 5) {
    return fields.concat(addressFieldNames)
  } else {
    return result
  }
}

function getAvailableFields(answers: IdentityFormData): Dict<FormInputField> {
  return { ...customFields(answers), ...builtInFields() }
}

function getFieldNames(configs: string[], omit: string[]): string[] {
  const configured = getConfiguredFieldNames(configs)
  const alwaysRequired = csvToArray('first_name, last_name, email')

  // Ensure the fields in the base form configuration are unique.
  // Array.from(new Set(...)) preserves order;
  //   - The position of first occurrence is respected.
  //   - Positions of duplicates are ignored.
  const result = new Set(prependUniqueItems(configured, alwaysRequired))

  for (const name of omit) {
    result.delete(name)
  }

  return Array.from(result)
}

function getConfiguredFieldNames(configurations: string[]): string[] {
  const { web, config } = environment
  const base = web.identity_form || config.identity_form || []

  const extrasConfigs = configurations.map(csvToArray).sort((a, b) => a.length - b.length)

  const longest = extrasConfigs.pop()

  if (!longest) {
    return base
  } else {
    const result = prependUniqueItems(longest, base)
    return extrasConfigs.reduce(appendUniqueItems, result)
  }
}
